import Phaser from 'phaser';
import { BootScene } from './scenes/BootScene';
import { LobbyScene } from './scenes/LobbyScene';
import { GameScene } from './scenes/GameScene';
import { ResultScene } from './scenes/ResultScene';
import { applySelectedTheme } from './features/FeatureState';
import './ui/modern-ui.css';

applySelectedTheme();

function isAppleMobileDevice(): boolean {
  const ua = String(typeof navigator !== 'undefined' ? navigator.userAgent : '').toLowerCase();
  const isiPhoneOrIPad = /iphone|ipad|ipod/.test(ua);
  const iPadOnDesktopUA = typeof navigator !== 'undefined'
    && navigator.platform === 'MacIntel'
    && navigator.maxTouchPoints > 1;
  return isiPhoneOrIPad || iPadOnDesktopUA;
}

// Phaser 3.60+ removed the `resolution` config. The supported way to render
// at native device pixels is to multiply the gameSize by devicePixelRatio
// and pull the canvas back down via CSS zoom. Cap at 3x to avoid blowing up
// fillrate on extreme-DPR devices (some Androids report 4 or higher).
function getDpr(): number {
  const raw = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;
  return Math.max(1, Math.min(raw, 3));
}

function getViewportSize(): { width: number; height: number } {
  const vv = window.visualViewport;
  const width = Math.max(1, Math.round(vv?.width ?? window.innerWidth));
  const height = Math.max(1, Math.round(vv?.height ?? window.innerHeight));
  return { width, height };
}

function createConfig(rendererType: number): Phaser.Types.Core.GameConfig {
  const { width, height } = getViewportSize();
  const dpr = getDpr();
  return {
    type: rendererType,
    width: Math.round(width * dpr),
    height: Math.round(height * dpr),
    backgroundColor: '#060611',
    parent: 'game-container',
    dom: {
      createContainer: true,
    },
    scale: {
      mode: Phaser.Scale.NONE,
      autoCenter: Phaser.Scale.NO_CENTER,
      zoom: 1 / dpr,
    },
    input: {
      activePointers: 3,
    },
    pixelArt: false,
    antialias: true,
    antialiasGL: true,
    scene: [BootScene, LobbyScene, GameScene, ResultScene],
  };
}

let game: Phaser.Game;
try {
  // WKWebView can fail WebGL init or touch scaling on some iOS devices; keep iOS on Canvas so the app boots and registers input predictably.
  const preferredRenderer = isAppleMobileDevice() ? Phaser.CANVAS : Phaser.AUTO;
  game = new Phaser.Game(createConfig(preferredRenderer));
} catch {
  game = new Phaser.Game(createConfig(Phaser.CANVAS));
}

// iOS WKWebView: explicit touch-action:none on the canvas so Phaser's
// non-passive touchstart listener fires without browser interference.
if (game.canvas) {
  game.canvas.style.touchAction = 'none';
}

let resizeScheduled = false;

const syncViewportSize = (): void => {
  const { width, height } = getViewportSize();
  const dpr = getDpr();
  const targetW = Math.round(width * dpr);
  const targetH = Math.round(height * dpr);
  const scale = game.scale;
  if (!scale) return;
  if (scale.gameSize.width === targetW && scale.gameSize.height === targetH) return;
  scale.resize(targetW, targetH);
};

const scheduleViewportSync = (): void => {
  if (resizeScheduled) return;
  resizeScheduled = true;
  window.requestAnimationFrame(() => {
    resizeScheduled = false;
    syncViewportSize();
    window.setTimeout(syncViewportSize, 120);
    window.setTimeout(syncViewportSize, 320);
  });
};

window.addEventListener('resize', scheduleViewportSync);
window.addEventListener('orientationchange', scheduleViewportSync);
window.visualViewport?.addEventListener('resize', scheduleViewportSync);
